import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { platformInfo, STATUS_STYLES, LEAD_STATUS_STYLES, inr } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, ReceiptText, Wallet, BellRing, CalendarCheck, TrendingUp, Package } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import BriefingCard from "@/components/BriefingCard";

const StatCard = ({ label, value, sub, icon: Icon, accent, testid, to }) => {
  const inner = (
    <Card data-testid={testid} className="p-5 border-gray-100 shadow-sm hover:-translate-y-0.5 transition-transform duration-200 h-full">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground font-medium">{label}</p>
          <p className="font-display text-2xl lg:text-3xl font-extrabold mt-1 text-gray-900 truncate">{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        <span className={`h-11 w-11 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center text-white shrink-0`}><Icon className="h-5 w-5" /></span>
      </div>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const b = stats?.billing || {};
  const showBilling = stats?.is_admin_or_billing;
  const platformData = (stats?.by_platform || []).map((p) => ({ name: platformInfo(p.platform).label.split(" ")[0], count: p.count, color: platformInfo(p.platform).color }));
  const leadEntries = Object.entries(stats?.leads_by_status || {});

  return (
    <DashboardLayout title={`Hi, ${user?.name?.split(" ")[0] || "there"} 👋`} subtitle="Here's what's happening at BEAUTWELLZ today">
      <BriefingCard />
      {!stats && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>}

      {stats && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {showBilling && <StatCard testid="stat-revenue" label="Total revenue" value={inr(b.revenue_total || 0)} sub={`Paid ${inr(b.revenue_paid || 0)} · Pending ${inr(b.revenue_pending || 0)}`} icon={Wallet} accent="from-emerald-500 to-teal-400" to="/billing" />}
            {showBilling && <StatCard testid="stat-invoices" label="Invoices" value={b.invoices_count || 0} sub={`${b.invoices_by_status?.Paid || 0} paid · ${b.invoices_by_status?.Issued || 0} issued`} icon={ReceiptText} accent="from-blue-600 to-indigo-500" to="/billing" />}
            <StatCard testid="stat-customers" label="Customers / Leads" value={stats.customers} icon={Users} accent="from-pink-500 to-rose-400" to="/customers" />
            <StatCard testid="stat-followups" label="Follow-ups due" value={stats.follow_ups_due} sub={stats.follow_ups_due > 0 ? "Needs attention today" : "All caught up"} icon={BellRing} accent="from-amber-500 to-orange-400" to="/customers" />
            {!showBilling && <StatCard testid="stat-posts" label="Planned posts" value={stats.posts} icon={CalendarCheck} accent="from-violet-500 to-purple-400" to="/calendar" />}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
            {showBilling ? (
              <Card className="lg:col-span-2 p-5 border-gray-100 shadow-sm">
                <h3 className="font-display font-semibold text-gray-900 flex items-center gap-2 mb-4"><TrendingUp className="h-4 w-4 text-emerald-600" /> Revenue (last 6 months)</h3>
                <div className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={b.monthly_revenue || []}>
                      <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={11} />
                      <YAxis tickLine={false} axisLine={false} fontSize={10} width={50} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                      <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} formatter={(v) => inr(v)} />
                      <Bar dataKey="revenue" radius={[6, 6, 0, 0]} fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            ) : (
              <Card className="lg:col-span-2 p-5 border-gray-100 shadow-sm">
                <h3 className="font-display font-semibold text-gray-900 flex items-center gap-2 mb-4"><TrendingUp className="h-4 w-4 text-blue-600" /> Posts by platform</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={platformData}>
                      <XAxis dataKey="name" tickLine={false} axisLine={false} fontSize={11} />
                      <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>{platformData.map((d, i) => <Cell key={i} fill={d.color} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}

            <Card className="p-5 border-gray-100 shadow-sm">
              <h3 className="font-display font-semibold text-gray-900 flex items-center gap-2 mb-3"><BellRing className="h-4 w-4 text-amber-500" /> Follow-ups due</h3>
              <div className="space-y-2">
                {(!stats.follow_ups || stats.follow_ups.length === 0) && <p className="text-sm text-muted-foreground">No follow-ups due. 🎉</p>}
                {stats.follow_ups?.map((f) => (
                  <Link key={f.id} to="/customers" className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50" data-testid={`followup-${f.id}`}>
                    <div className="min-w-0"><p className="text-sm font-medium truncate text-gray-900">{f.name}</p><p className="text-[11px] text-muted-foreground">{f.phone || "no phone"}</p></div>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">{f.follow_up_date}</Badge>
                  </Link>
                ))}
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
            <Card className="p-5 border-gray-100 shadow-sm">
              <h3 className="font-display font-semibold text-gray-900 mb-3">Lead pipeline</h3>
              <div className="space-y-2">
                {leadEntries.length === 0 && <p className="text-sm text-muted-foreground">No leads yet.</p>}
                {leadEntries.map(([st, n]) => (
                  <div key={st} className="flex items-center justify-between" data-testid={`lead-stat-${st.toLowerCase()}`}>
                    <Badge variant="outline" className={`${LEAD_STATUS_STYLES[st] || "bg-gray-100 text-gray-600"} text-[11px]`}>{st}</Badge>
                    <span className="text-sm font-semibold">{n}</span>
                  </div>
                ))}
              </div>
            </Card>

            {showBilling && (
              <Card className="p-5 border-gray-100 shadow-sm">
                <h3 className="font-display font-semibold text-gray-900 flex items-center gap-2 mb-3"><Package className="h-4 w-4 text-blue-600" /> Top products (by value)</h3>
                <div className="space-y-2">
                  {(!b.top_products || b.top_products.length === 0) && <p className="text-sm text-muted-foreground">No sales yet.</p>}
                  {b.top_products?.map((p, i) => (
                    <div key={i} className="flex items-center justify-between" data-testid={`top-product-${i}`}>
                      <span className="text-sm truncate max-w-[60%] text-gray-700">{p.name}</span>
                      <span className="text-sm font-semibold">{inr(p.value)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <Card className="p-5 border-gray-100 shadow-sm">
              <h3 className="font-display font-semibold text-gray-900 mb-3">Upcoming posts</h3>
              <div className="space-y-2">
                {stats.upcoming?.length === 0 && <p className="text-sm text-muted-foreground">No upcoming posts. Plan one!</p>}
                {stats.upcoming?.map((p) => {
                  const info = platformInfo(p.platform);
                  return (
                    <Link key={p.id} to={`/social/${p.platform}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50" data-testid={`upcoming-${p.id}`}>
                      <span className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: info.color + "22" }}><info.icon className="h-4 w-4" style={{ color: info.color }} /></span>
                      <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate text-gray-900">{p.title}</p><p className="text-xs text-muted-foreground">{p.date}</p></div>
                      <Badge variant="outline" className={`${STATUS_STYLES[p.status]} text-[10px]`}>{p.status}</Badge>
                    </Link>
                  );
                })}
              </div>
            </Card>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
